if (!customElements.get('product-show-more')) {
  customElements.define(
    'product-show-more',
    class ProductShowMore extends HTMLElement {
      constructor() {
        super();

        this.expanded = false;
        this.animationTimer = null;
        this.resizeFrame = null;

        this.onButtonClick = this.onButtonClick.bind(this);
        this.onDetailsToggle = this.onDetailsToggle.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.dataset.initialized = 'true';

        this.content = this.querySelector(
          '[data-collapsible-show-more-content]'
        );

        this.button = this.querySelector(
          '[data-collapsible-show-more-button]'
        );

        this.showMoreLabel = this.querySelector(
          '[data-show-more-label]'
        );

        this.showLessLabel = this.querySelector(
          '[data-show-less-label]'
        );

        this.details = this.closest('details');

        this.collapsedHeight =
          Number.parseInt(this.dataset.collapsedHeight, 10) || 220;

        if (!this.content || !this.button) return;

        this.button.addEventListener(
          'click',
          this.onButtonClick
        );

        if (this.details) {
          this.details.addEventListener(
            'toggle',
            this.onDetailsToggle
          );
        }

        window.addEventListener(
          'resize',
          this.onResize
        );

        this.images = Array.from(
          this.content.querySelectorAll('img')
        );

        this.images.forEach((image) => {
          if (!image.complete) {
            image.addEventListener(
              'load',
              this.onResize
            );
          }
        });

        if (!this.details || this.details.open) {
          window.requestAnimationFrame(() => {
            this.measure();
          });
        }
      }

      disconnectedCallback() {
        if (this.button) {
          this.button.removeEventListener(
            'click',
            this.onButtonClick
          );
        }

        if (this.details) {
          this.details.removeEventListener(
            'toggle',
            this.onDetailsToggle
          );
        }

        window.removeEventListener(
          'resize',
          this.onResize
        );

        if (this.images) {
          this.images.forEach((image) => {
            image.removeEventListener(
              'load',
              this.onResize
            );
          });
        }

        if (this.animationTimer) {
          window.clearTimeout(this.animationTimer);
        }

        if (this.resizeFrame) {
          window.cancelAnimationFrame(
            this.resizeFrame
          );
        }

        delete this.dataset.initialized;
      }

      onButtonClick(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.classList.contains('has-overflow')) {
          return;
        }

        if (this.expanded) {
          this.collapse();
        } else {
          this.expand();
        }
      }

      onDetailsToggle() {
        if (!this.details || !this.details.open) return;

        window.requestAnimationFrame(() => {
          this.measure();
        });
      }

      onResize() {
        if (this.resizeFrame) {
          window.cancelAnimationFrame(
            this.resizeFrame
          );
        }

        this.resizeFrame =
          window.requestAnimationFrame(() => {
            if (!this.details || this.details.open) {
              this.measure();
            }

            this.resizeFrame = null;
          });
      }

      measure() {
        if (
          this.details &&
          !this.details.open
        ) {
          return;
        }

        this.content.style.maxHeight = 'none';

        const fullHeight = this.content.scrollHeight;

        const hasOverflow =
          fullHeight > this.collapsedHeight + 4;

        this.classList.toggle(
          'has-overflow',
          hasOverflow
        );

        if (!hasOverflow) {
          this.expanded = false;

          this.classList.remove(
            'is-expanded'
          );

          this.content.style.maxHeight = 'none';
          this.button.hidden = true;

          this.updateButton(false);
          return;
        }

        this.button.hidden = false;

        if (this.expanded) {
          this.classList.add(
            'is-expanded'
          );

          this.content.style.maxHeight = 'none';
          this.updateButton(true);
        } else {
          this.classList.remove(
            'is-expanded'
          );

          this.content.style.maxHeight =
            `${this.collapsedHeight}px`;

          this.updateButton(false);
        }
      }

      expand() {
        if (this.animationTimer) {
          window.clearTimeout(this.animationTimer);
        }

        this.expanded = true;

        const startingHeight =
          this.content.getBoundingClientRect().height;

        this.content.style.maxHeight =
          `${startingHeight}px`;

        void this.content.offsetHeight;

        this.classList.add('is-expanded');
        this.updateButton(true);

        const fullHeight = this.content.scrollHeight;

        window.requestAnimationFrame(() => {
          this.content.style.maxHeight =
            `${fullHeight}px`;
        });

        this.animationTimer =
          window.setTimeout(() => {
            if (this.expanded) {
              this.content.style.maxHeight = 'none';
            }
          }, 350);
      }

      collapse() {
        if (this.animationTimer) {
          window.clearTimeout(this.animationTimer);
        }

        this.expanded = false;

        const fullHeight = this.content.scrollHeight;

        this.content.style.maxHeight =
          `${fullHeight}px`;

        void this.content.offsetHeight;

        this.classList.remove('is-expanded');
        this.updateButton(false);

        window.requestAnimationFrame(() => {
          this.content.style.maxHeight =
            `${this.collapsedHeight}px`;
        });
      }

      updateButton(expanded) {
        this.button.setAttribute(
          'aria-expanded',
          expanded ? 'true' : 'false'
        );

        if (this.showMoreLabel) {
          this.showMoreLabel.hidden = expanded;
        }

        if (this.showLessLabel) {
          this.showLessLabel.hidden = !expanded;
        }
      }
    }
  );
}